/**
 * Lowthian Design - slideshow accessibility supplement
 *
 * Closes two gaps in Horizon's <slideshow-component> that matter once autoplay
 * is switched on. Both were verified against assets/slideshow.js on 2026-07-30.
 *
 * 1. PAUSE ON KEYBOARD FOCUS
 *    Horizon pauses on hover (mouseenter -> suspend, mouseleave -> resume, plus a
 *    :hover guard inside the autoplay interval) but binds NO focus listeners at all.
 *    A keyboard user tabbing to a slide's "Shop selection" button would have the
 *    slider advance out from under them mid-read. Focus parity with hover is the rule.
 *
 * 2. prefers-reduced-motion DOES NOT STOP AUTOPLAY
 *    Horizon honours the query for scroll-behavior only (snippets/slideshow-styles.liquid
 *    drops `smooth` to `auto`). The autoplay timer keeps running regardless, so a
 *    reduced-motion user still gets content moving on its own. We stop it outright and
 *    leave the manual arrows and dots fully working.
 *
 * 3. aria-live IS BACKWARDS FOR AN AUTO-ROTATING CAROUSEL
 *    snippets/slideshow.liquid stamps aria-live="polite" on the host ONLY when autoplay
 *    is on. That is the reverse of the ARIA Authoring Practices carousel pattern: while
 *    content rotates on its own, every advance is announced unprompted - slide churn. The
 *    APG guidance is aria-live="off" while auto-rotating, switching to "polite" once
 *    rotation stops, so that USER-initiated slide changes are announced and automatic ones
 *    are not. We keep it in sync with the paused state.
 *
 * Deliberately NOT reimplemented here: the pause/play button (Horizon already renders a
 * real <button> with an aria-label when autoplay is on) and hover pausing.
 *
 * Uses Horizon's own public API - suspend() / resume() / pause() - rather than touching
 * private state, so it degrades harmlessly if the component changes.
 *
 * Revisions
 * 2026-07-30  Kai  Added aria-live sync (item 3) after tracing that Horizon stamps
 *                   aria-live="polite" precisely when autoplay is on.
 * 2026-07-30  Kai  Created.
 */
(function () {
  'use strict';

  var REDUCED = '(prefers-reduced-motion: reduce)';

  function prefersReducedMotion() {
    return window.matchMedia && window.matchMedia(REDUCED).matches;
  }

  function enhance(slideshow) {
    if (!slideshow || slideshow.dataset.ldEnhanced === 'true') return;
    slideshow.dataset.ldEnhanced = 'true';

    // --- 2. reduced motion: stop auto-advance, keep manual controls ---
    if (prefersReducedMotion()) {
      if (typeof slideshow.pause === 'function') slideshow.pause();
      slideshow.setAttribute('data-ld-reduced-motion', '');
      // Rotation is stopped, so user-driven slide changes SHOULD be announced.
      slideshow.setAttribute('aria-live', 'polite');
      return; // nothing auto-advances, so focus pausing is moot
    }

    // --- 3. aria-live follows the rotation state ---
    var setLive = function (value) {
      if (slideshow.getAttribute('aria-live') !== value) {
        slideshow.setAttribute('aria-live', value);
      }
    };

    var syncLive = function () {
      // Announce slide changes only when rotation is NOT running on its own.
      var rotating = slideshow.hasAttribute('autoplay') && !slideshow.hasAttribute('paused');
      setLive(rotating ? 'off' : 'polite');
    };

    syncLive();

    // Horizon toggles its own `paused` attribute from the pause/play button.
    if (window.MutationObserver) {
      new MutationObserver(syncLive).observe(slideshow, {
        attributes: true,
        attributeFilter: ['paused', 'autoplay'],
      });
    }

    // --- 1. pause on keyboard focus, resume when focus leaves ---
    // focusin/focusout bubble, unlike focus/blur, so one pair on the host covers
    // every control and link inside any slide.
    slideshow.addEventListener('focusin', function () {
      if (typeof slideshow.suspend === 'function') slideshow.suspend();
      slideshow.setAttribute('data-ld-focus-paused', '');
      // A keyboard user driving the slideshow should hear the slide they land on.
      setLive('polite');
    });

    slideshow.addEventListener('focusout', function (event) {
      // Ignore focus moving between elements that are still inside the slideshow.
      if (event.relatedTarget && slideshow.contains(event.relatedTarget)) return;
      slideshow.removeAttribute('data-ld-focus-paused');
      // Never override a deliberate press of the pause button: resume() is a no-op
      // while Horizon's own `paused` attribute is set.
      if (typeof slideshow.resume === 'function') slideshow.resume();
      syncLive();
    });
  }

  function enhanceAll(root) {
    var scope = root && root.querySelectorAll ? root : document;
    scope.querySelectorAll('slideshow-component').forEach(enhance);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      enhanceAll(document);
    });
  } else {
    enhanceAll(document);
  }

  // Theme editor re-renders sections without a full page load.
  document.addEventListener('shopify:section:load', function (event) {
    enhanceAll(event.target || document);
  });

  // If the user flips the OS setting while the page is open, honour it immediately.
  if (window.matchMedia) {
    var mq = window.matchMedia(REDUCED);
    var onChange = function () {
      document.querySelectorAll('slideshow-component').forEach(function (s) {
        if (mq.matches) {
          if (typeof s.pause === 'function') s.pause();
          s.setAttribute('data-ld-reduced-motion', '');
        } else {
          s.removeAttribute('data-ld-reduced-motion');
        }
      });
    };
    if (mq.addEventListener) mq.addEventListener('change', onChange);
  }
})();
